import { AlertTriangle, Check, Copy, RefreshCw } from "lucide-react";

import type {
  CardHoldingWallet,
  CardHoldingWalletAsset,
} from "@/modules/dashboard/cards/types/wallets-api.types";
import {
  formatAssetList,
  formatDepositNetwork,
  splitAddressForVerification,
} from "@/modules/dashboard/cards/utils/cardDeposit";
import { getColorForCurrency } from "@/modules/dashboard/wallets/constants/walletColors.constants";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaNetworkIcon, ColocaTokenIcon } from "@/shared/components/data-display/ColocaCryptoIcon";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import { ColocaTooltip } from "@/shared/components/overlays/ColocaTooltip";
import { useCopyToClipboard } from "@/shared/hooks/useCopyToClipboard";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

const MICRO_LABEL = "font-mono text-[10px] uppercase tracking-wider text-muted-foreground";

export interface HoldingWalletPanelProps {
  wallet: CardHoldingWallet | null;
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  className?: string;
}

/**
 * Where the money comes in: the network, the address and the assets that address takes.
 *
 * It heads the quoter because it is the half the operator dictates to the client — the figures below it are
 * worthless without somewhere to send them. The pool's own balance is not here, and the endpoint does not
 * return it: it is Coloca float and belongs on no screen that exists to answer a client's question.
 *
 * Presentational — every state arrives as a prop, so there is a story for each.
 */
export function HoldingWalletPanel({
  wallet,
  isLoading,
  error,
  onRetry,
  className,
}: HoldingWalletPanelProps) {
  const { t, activeLanguage } = useTranslations("cardWallets");
  const { copiedValue, copy } = useCopyToClipboard();

  if (error !== null && !wallet) {
    return (
      <ColocaAlert
        variant="destructive"
        icon={AlertTriangle}
        className={className}
        text={t("quote_pool_error")}
        actions={
          <ColocaButton variant="default" size="sm" icon={RefreshCw} onClick={onRetry}>
            {t("quote_pool_retry")}
          </ColocaButton>
        }
      />
    );
  }

  if (!wallet) {
    return <HoldingWalletSkeleton className={className} />;
  }

  const network = formatDepositNetwork(wallet.blockchain);
  const { head, middle, tail } = splitAddressForVerification(wallet.address);
  const symbols = wallet.assets.map((asset) => asset.symbol);
  const copied = copiedValue === wallet.address;

  return (
    <ColocaSurface
      variant="muted"
      padding="lg"
      aria-busy={isLoading || undefined}
      className={cn("space-y-4", className)}
    >
      <dl className="grid gap-4 sm:grid-cols-2">
        <div className="min-w-0">
          <dt className={MICRO_LABEL}>{t("quote_network_label")}</dt>
          <dd className="mt-1.5 flex items-center gap-2 text-sm font-medium text-foreground">
            <ColocaNetworkIcon network={wallet.blockchain} size={20} />
            <span>{network}</span>
          </dd>
        </div>

        {wallet.assets.length > 0 && (
          <div className="min-w-0">
            <dt className={MICRO_LABEL}>{t("quote_assets_label")}</dt>
            <dd className="mt-1.5 flex flex-wrap gap-1.5">
              {wallet.assets.map((asset) => (
                <AssetChip key={asset.symbol} asset={asset} />
              ))}
            </dd>
          </div>
        )}

        <div className="min-w-0 sm:col-span-2">
          <dt className={MICRO_LABEL}>{t("quote_address_label")}</dt>
          {/* The row wraps rather than shrinking: the button is `shrink-0`, so on a narrow dialog it drops
              under the address instead of being clipped out of the panel (design-system rule 15). */}
          <dd className="mt-1.5 flex flex-wrap items-start gap-2">
            {/* Whole, never truncated. The ends a person checks against their wallet carry full weight; the
                middle recedes but stays readable. */}
            <p className="min-w-0 flex-1 break-all font-mono text-sm leading-relaxed text-foreground">
              <span className="font-semibold">{head}</span>
              <span className="text-foreground/60">{middle}</span>
              <span className="font-semibold">{tail}</span>
            </p>
            <ColocaIconButton
              variant="secondary"
              size="sm"
              icon={copied ? Check : Copy}
              aria-label={t("quote_address_copy_aria")}
              onClick={() => void copy(wallet.address, t("quote_address_copied"))}
            />
          </dd>
        </div>
      </dl>

      {symbols.length > 0 && (
        <ColocaAlert
          variant="warning"
          icon={AlertTriangle}
          text={t("quote_pool_warning", { assets: formatAssetList(symbols, activeLanguage), network })}
        />
      )}
    </ColocaSurface>
  );
}

/** The symbol, with the contract behind it — the only thing that tells two same-named tokens apart. */
function AssetChip({ asset }: { asset: CardHoldingWalletAsset }) {
  const { t } = useTranslations("cardWallets");
  return (
    <ColocaTooltip
      triggerRender={
        <button
          type="button"
          className={cn(
            "inline-flex h-7 items-center gap-1.5 rounded-full border border-border bg-background pl-1.5 pr-2.5",
            "font-mono text-xs font-medium text-foreground transition-colors hover:bg-muted",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          <ColocaTokenIcon
            symbol={asset.symbol}
            size={16}
            fallback={
              <span
                aria-hidden="true"
                className="ml-1 size-2 rounded-full"
                style={{ backgroundColor: getColorForCurrency(asset.symbol) }}
              />
            }
          />
          {asset.symbol}
        </button>
      }
    >
      <span className="break-all font-mono text-[10px] opacity-80">
        {t("quote_asset_contract", { address: asset.contract_address })}
      </span>
    </ColocaTooltip>
  );
}

function HoldingWalletSkeleton({ className }: { className?: string }) {
  return (
    <ColocaSurface variant="muted" padding="lg" className={cn("space-y-4", className)} aria-hidden="true">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <ColocaSkeleton className="h-2.5 w-10" />
          <ColocaSkeleton className="h-5 w-24" />
        </div>
        <div className="space-y-1.5">
          <ColocaSkeleton className="h-2.5 w-28" />
          <div className="flex gap-1.5">
            <ColocaSkeleton className="h-7 w-16 rounded-full" />
            <ColocaSkeleton className="h-7 w-16 rounded-full" />
          </div>
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <ColocaSkeleton className="h-2.5 w-32" />
          <ColocaSkeleton className="h-5 w-full" />
        </div>
      </div>
    </ColocaSurface>
  );
}
